(() => {
  if (!window.customElements || customElements.get('ecom-offer-hub')) return;

  class EcomOfferHub extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.events = { signal: this.controller.signal };
      this.canvas = this.querySelector('[data-offer-canvas]');
      this.stage = this.querySelector('[data-offer-scratch-stage]');
      this.result = this.querySelector('[data-offer-result]');
      this.status = this.querySelector('[data-offer-status]');
      this.spinButton = this.querySelector('[data-offer-spin]');
      this.wheel = this.querySelector('[data-offer-wheel]');
      if (!this.result || !this.status || !this.spinButton || !this.wheel) return;
      this.querySelector('[data-offer-reveal]')?.addEventListener('click', () => this.reveal('scratch'), this.events);
      this.spinButton.addEventListener('click', () => this.spin(), this.events);
      this.querySelector('[data-offer-copy]')?.addEventListener('click', () => this.copy(), this.events);
      this.querySelector('[data-offer-games]').hidden = false;
      this.prepareScratch();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      clearTimeout(this.spinTimer);
      this.controller = null;
      this.pointer = null;
      this.spinning = false;
      this.wheel?.classList.remove('is-spinning');
      if (this.spinButton) {
        this.spinButton.disabled = false;
        this.spinButton.removeAttribute('aria-busy');
      }
    }

    prepareScratch() {
      if (!this.canvas || !this.stage) return;
      try { this.context = this.canvas.getContext('2d', { willReadFrequently: true }); } catch { return; }
      if (!this.context) return;
      this.scratchReady = true;
      this.paintScratch();
      this.canvas.addEventListener('pointerdown', (event) => {
        if (this.scratchDone || this.pointer !== null && this.pointer !== undefined || event.isPrimary === false || event.button > 0) return;
        this.pointer = event.pointerId;
        this.lastPoint = this.point(event);
        this.canvas.setPointerCapture?.(event.pointerId);
        this.erase(this.lastPoint, this.lastPoint);
        this.querySelector('[data-offer-scratch-label]').hidden = true;
      }, this.events);
      this.canvas.addEventListener('pointermove', (event) => {
        if (this.pointer !== event.pointerId || this.scratchDone) return;
        const current = this.point(event);
        this.erase(this.lastPoint, current);
        this.lastPoint = current;
        if (!this.lastCheck || event.timeStamp - this.lastCheck > 160) {
          this.lastCheck = event.timeStamp;
          this.checkCoverage();
        }
      }, this.events);
      const stop = (event) => {
        if (this.pointer !== event.pointerId) return;
        this.pointer = null;
        this.lastPoint = null;
        if (this.canvas.hasPointerCapture?.(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
        this.checkCoverage();
      };
      this.canvas.addEventListener('pointerup', stop, this.events);
      this.canvas.addEventListener('pointercancel', stop, this.events);
      this.canvas.addEventListener('lostpointercapture', stop, this.events);
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.scratchDone) return;
          const width = Math.round(this.canvas.getBoundingClientRect().width);
          if (width && width !== this.scratchWidth) this.paintScratch();
        });
        this.resizeObserver.observe(this.canvas);
      }
    }

    paintScratch() {
      if (!this.context || this.scratchDone) return;
      const rect = this.canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      this.scratchWidth = Math.round(rect.width);
      this.canvas.width = Math.round(rect.width * ratio);
      this.canvas.height = Math.round(rect.height * ratio);
      this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
      this.context.globalCompositeOperation = 'source-over';
      this.context.fillStyle = '#171715';
      this.context.fillRect(0, 0, rect.width, rect.height);
      this.context.strokeStyle = '#45443e';
      this.context.lineWidth = 1;
      for (let x = -rect.height; x < rect.width; x += 18) {
        this.context.beginPath();
        this.context.moveTo(x, 0);
        this.context.lineTo(x + rect.height, rect.height);
        this.context.stroke();
      }
      this.pointer = null;
      this.lastPoint = null;
      this.lastCheck = 0;
    }

    point(event) {
      const rect = this.canvas.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    }

    erase(from, to) {
      const context = this.context;
      context.globalCompositeOperation = 'destination-out';
      context.lineWidth = 46;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.beginPath();
      context.moveTo(from.x, from.y);
      context.lineTo(to.x, to.y);
      context.stroke();
      context.beginPath();
      context.arc(to.x, to.y, 23, 0, Math.PI * 2);
      context.fill();
    }

    checkCoverage() {
      if (!this.context || this.scratchDone) return;
      try {
        const pixels = this.context.getImageData(0, 0, this.canvas.width, this.canvas.height).data;
        let cleared = 0;
        let sampled = 0;
        for (let index = 3; index < pixels.length; index += 64) {
          sampled += 1;
          if (pixels[index] < 128) cleared += 1;
        }
        if (sampled && cleared / sampled >= 0.4) this.reveal('scratch');
      } catch {
        this.scratchReady = false;
      }
    }

    reveal(source) {
      if (source === 'scratch') {
        this.scratchDone = true;
        this.stage?.classList.add('is-scratched');
        this.pointer = null;
      }
      this.result.hidden = false;
      this.status.textContent = this.dataset.offerRevealedLabel;
    }

    spin() {
      if (this.spinning) return;
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        this.reveal('spin');
        return;
      }
      this.spinning = true;
      this.spinButton.disabled = true;
      this.spinButton.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.offerSpinningLabel;
      const finish = () => {
        if (!this.spinning) return;
        this.spinning = false;
        clearTimeout(this.spinTimer);
        this.wheel.removeEventListener('animationend', finish);
        this.wheel.classList.remove('is-spinning');
        this.spinButton.disabled = false;
        this.spinButton.removeAttribute('aria-busy');
        this.reveal('spin');
      };
      this.wheel.addEventListener('animationend', finish, this.events);
      this.wheel.classList.add('is-spinning');
      this.spinTimer = setTimeout(finish, 3000);
    }

    async copy() {
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(this.dataset.offerCode);
        this.status.textContent = this.dataset.offerCopiedLabel;
      } catch {
        this.status.textContent = this.dataset.offerCopyFailedLabel;
      }
    }
  }

  customElements.define('ecom-offer-hub', EcomOfferHub);
})();
